/**
 * Fault Pipeline Service
 *
 * Handles all business logic for the Fault Pipeline:
 * - Creating and managing faults
 * - Stage transitions
 * - Stage 04 Handoff (archive fault + create deployment job)
 * - Investigation history tracking
 * - Tech consult notifications
 */

import { getLocaleSync } from '@/utils/locale';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  increment,
  arrayUnion,
  runTransaction,
} from 'firebase/firestore';
import { db } from '../firebase';
import { buildJobEvent } from '@/services/job-event.service';
import type {
  Fault,
  FaultStage,
  FaultCosts,
  FaultMaterialItem,
  FaultReopenEntry,
  RemoteLog,
  CallNote,
  ClientUpdate,
  InvestigationHistoryEntry,
  JobBriefingHistory,
} from '../types/fault.types';
import { FAULT_STAGE_ORDER, FAULT_STAGE_LABELS, migrateFaultStage } from '../types/fault.types';
import type { Job, FieldLogEntry, JobPhoto, Material } from '../types/schema';
import { getCheckpointsForNewJob } from '@/services/checklist-template.service';
import { getTenantIdSync, tenantIdReadUnion } from '@/lib/tenant';
import { createTicketWithNumber } from '@/services/ticket-number.service';
import { logCustomerActivity, resolveActorName } from '@/services/aggregate-customer.service';
import { geocodeAddress } from '@/services/geocode.service';
import { JOB_FALLBACK_COORDS } from '@/data/map-config';
import { SLA_TARGETS, LABOUR_RATE_PER_HOUR } from '@/config/sla.config';
import { stripUndefinedDeep } from '@/utils/firestore';
import { addressSuburb } from '@/utils/address';
import {
  buildBillingCaptureUpdate,
  type BillingCaptureInput,
} from '@/services/billing-capture';

/**
 * Resolve coordinates for a new job derived from a fault. Prefers lat/lng
 * already on the fault, then geocodes the fault's address via Google, then
 * falls back to the shared Bowral default (map backfill retries later).
 */
async function resolveFaultJobCoords(
  fault: Fault,
): Promise<{ lat: number; lng: number; formatted?: string }> {
  const fLat = fault.lat;
  const fLng = fault.lng;
  if (typeof fLat === 'number' && typeof fLng === 'number') {
    return { lat: fLat, lng: fLng };
  }
  if (fault.address) {
    const r = await geocodeAddress(fault.address).catch(() => null);
    if (r) return { lat: r.lat, lng: r.lng, formatted: r.formattedAddress };
  }
  return { lat: JOB_FALLBACK_COORDS.lat, lng: JOB_FALLBACK_COORDS.lng };
}

/**
 * Recalculate cost totals from current labour minutes and materials
 */
function recalculateCosts(costs: FaultCosts): FaultCosts {
  const labourCost = Math.round((costs.labourMinutes / 60) * LABOUR_RATE_PER_HOUR * 100) / 100;
  const materialsCost = Math.round(
    costs.materials.reduce((sum: number, m: FaultMaterialItem) => sum + m.quantity * m.unitCost, 0) * 100
  ) / 100;
  return {
    ...costs,
    labourCost,
    materialsCost,
    totalCost: Math.round((labourCost + materialsCost) * 100) / 100,
  };
}

/**
 * Create a new fault ticket (Stage 01-Triage)
 */
export async function createFault(faultData: {
  customerId: string;
  clientName: string;
  // Service linking (Phase 6: Enhanced)
  serviceId?: string;
  serviceName?: string;
  serviceType?: string;
  servicePlanName?: string;
  serviceMonthlyPrice?: number;
  serviceActivatedAt?: string;
  address: string;
  lat?: number;
  lng?: number;
  intakeMethod: 'Manual' | 'Auto' | 'live_call' | 'email';
  intakeNotes: string;
  intakeBy: string;
  callDuration?: number;
  symptom: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  deviceInfo?: Fault['deviceInfo'];
  affectedServices?: string[];
}): Promise<string> {
  if (!faultData.customerId || faultData.customerId === 'manual-entry') {
    throw new Error('A valid customerId is required to create a fault');
  }
  if (!faultData.clientName?.trim()) {
    throw new Error('Client name is required to create a fault');
  }

  const now = new Date().toISOString();
  const nowMs = new Date(now).getTime();

  // Calculate SLA deadlines based on severity
  const slaTarget = SLA_TARGETS[faultData.severity];
  const slaResponseDeadline = new Date(nowMs + slaTarget.responseMinutes * 60 * 1000).toISOString();
  const slaResolutionDeadline = new Date(nowMs + slaTarget.resolutionHours * 60 * 60 * 1000).toISOString();

  const fault: Omit<Fault, 'id'> = {
    ...faultData,
    tenantId: getTenantIdSync(),
    stage: '01-Triage',
    intakeDate: now,
    remoteLogs: [],
    callNotes: [],
    clientUpdates: [],
    investigationHistory: [
      {
        id: crypto.randomUUID(),
        timestamp: now,
        stage: '01-Triage',
        author: faultData.intakeBy,
        action: 'Fault Created',
        details: `Intake Method: ${faultData.intakeMethod}${faultData.callDuration ? ` (Call: ${Math.floor(faultData.callDuration / 60)}m ${faultData.callDuration % 60}s)` : ''}. ${faultData.intakeNotes}`,
      },
    ],
    consultRequested: false,
    archived: false,
    slaResponseDeadline,
    slaResolutionDeadline,
    costs: recalculateCosts({
      labourMinutes: faultData.callDuration ? Math.ceil(faultData.callDuration / 60) : 0,
      labourCost: 0,
      materials: [],
      materialsCost: 0,
      totalCost: 0,
    }),
    createdAt: now,
    updatedAt: now,
  };

  // Same transaction reserves the FLT-NNNN ticket number and writes the doc.
  // TWIN: functions/src/mcp/faults.ts raiseFault.
  const { id: faultId } = await createTicketWithNumber('fault', getTenantIdSync(), fault);

  // Fire-and-forget: Repeat fault detection
  detectRepeatFaults(faultId, faultData, now).catch((err: unknown) =>
    console.error('[RepeatDetection] Failed:', err)
  );

  // Fire-and-forget: Sync fault stats to linked customer service
  if (faultData.serviceId) {
    syncFaultStatsToService(faultData.serviceId, faultData.symptom, now).catch((err: unknown) =>
      console.error('[FaultServiceSync] Failed:', err)
    );
  }

  return faultId;
}

/**
 * Detect repeat faults for the same customer within the last 60 days.
 * Matches on: same customerId AND (similar symptom OR same serviceId OR same device serial).
 * If 2+ related faults found, auto-escalates severity and flags the fault.
 */
async function detectRepeatFaults(
  faultId: string,
  faultData: {
    customerId: string;
    symptom: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
    serviceId?: string;
    deviceInfo?: Fault['deviceInfo'];
    intakeBy: string;
  },
  now: string
): Promise<void> {
  const sixtyDaysAgo = new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString();

  // Query faults for same customer in last 60 days
  const q = query(
    collection(db, 'faults'),
    where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
    where('customerId', '==', faultData.customerId),
    where('createdAt', '>=', sixtyDaysAgo),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);
  const candidateFaults = snapshot.docs
    .map((d) => ({ id: d.id, ...d.data() } as Fault))
    .filter((f: Fault) => f.id !== faultId && f.resolutionType !== 'closed_duplicate');

  if (candidateFaults.length === 0) return;

  // Match on similar symptom, same serviceId, or same device serial
  const symptomWords = faultData.symptom.toLowerCase().split(/\s+/);
  const serial = faultData.deviceInfo?.serialNumber;

  const relatedFaults = candidateFaults.filter((f: Fault) => {
    // Same serviceId
    if (faultData.serviceId && f.serviceId && faultData.serviceId === f.serviceId) return true;
    // Same device serial number
    if (serial && f.deviceInfo?.serialNumber && serial === f.deviceInfo.serialNumber) return true;
    // Similar symptom (share any significant word)
    const otherWords = f.symptom.toLowerCase().split(/\s+/);
    const shared = symptomWords.filter((w: string) => w.length > 3 && otherWords.includes(w));
    if (shared.length > 0) return true;
    return false;
  });

  if (relatedFaults.length === 0) return;

  const repeatCount = relatedFaults.length + 1; // Including this new fault
  const repeatFlag = relatedFaults.length >= 2; // 2+ related = flagged (3+ total)

  const relatedFaultData = relatedFaults.map((f: Fault) => ({
    faultId: f.id!,
    symptom: f.symptom,
    createdAt: f.createdAt,
    resolutionType: f.resolutionType || '',
  }));

  // Build update
  const updates: Record<string, unknown> = {
    relatedFaults: relatedFaultData,
    repeatFlag,
    repeatCount,
    updatedAt: now,
  };

  // Auto-escalate severity if repeat flag triggered
  if (repeatFlag) {
    const severityEscalation: Record<string, string> = {
      low: 'medium',
      medium: 'high',
      high: 'critical',
      critical: 'critical',
    };
    const escalatedSeverity = severityEscalation[faultData.severity];
    if (escalatedSeverity !== faultData.severity) {
      updates.severity = escalatedSeverity;

      // Recalculate SLA deadlines for escalated severity
      const newSlaTarget = SLA_TARGETS[escalatedSeverity as keyof typeof SLA_TARGETS];
      const nowMs = new Date(now).getTime();
      updates.slaResponseDeadline = new Date(nowMs + newSlaTarget.responseMinutes * 60 * 1000).toISOString();
      updates.slaResolutionDeadline = new Date(nowMs + newSlaTarget.resolutionHours * 60 * 60 * 1000).toISOString();
    }

    // Calculate days span
    const oldestRelated = relatedFaults[relatedFaults.length - 1];
    const daySpan = Math.floor((Date.now() - new Date(oldestRelated.createdAt).getTime()) / (24 * 60 * 60 * 1000));

    // Add investigation history entry
    const faultRef = doc(db, 'faults', faultId);
    const faultSnap = await getDoc(faultRef);
    if (faultSnap.exists()) {
      const repeatHistoryEntry: InvestigationHistoryEntry = {
        id: crypto.randomUUID(),
        timestamp: now,
        stage: '01-Triage',
        author: 'System',
        action: 'Repeat Issue Detected',
        details: `Auto-flagged as repeat issue. ${repeatCount}${repeatCount === 2 ? 'nd' : repeatCount === 3 ? 'rd' : 'th'} fault for this client in ${daySpan} days. Related faults: ${relatedFaultData.map((r) => r.faultId).join(', ')}`,
      };
      updates.investigationHistory = arrayUnion(repeatHistoryEntry);
    }
  }

  await updateDoc(doc(db, 'faults', faultId), updates);
}

/**
 * Remote investigation is now part of the intake process.
 * Use addRemoteLog() to add diagnostics during intake.
 */

/**
 * Add a remote log entry (diagnostic, reboot, notes, etc.)
 */
/**
 * Fire-and-forget @mention fanout for fault note surfaces (call notes,
 * remote logs, client updates). These writers previously had no mention
 * support — "@casey" typed into a fault call note never lit anyone's bell.
 * Mirrors the unified notes.service pattern; failure never blocks the write.
 */
async function fanoutFaultNoteMentions(
  faultId: string,
  fault: Fault,
  content: string,
  authorName: string,
  sourceNoteId?: string,
): Promise<void> {
  if (!content || !content.includes('@')) return;
  try {
    const { extractMentions, createMentions } = await import('./mention.service');
    // Mirror the useTeam pattern — users collection has pre-tenant docs, so
    // no tenant filter on the read (names are only used for @match resolution).
    const usersSnap = await getDocs(collection(db, 'users'));
    const availableUsers = usersSnap.docs
      .map((d) => {
        const u = d.data() as { name?: string; firstName?: string; lastName?: string };
        return { id: d.id, name: u.name || '', firstName: u.firstName, lastName: u.lastName };
      })
      .filter((u) => u.name);
    if (extractMentions(content, availableUsers).length === 0) return;
    const { MentionEntityType } = await import('../types/mention.types');
    await createMentions({
      noteContent: content,
      entityType: MentionEntityType.FAULT,
      entityId: faultId,
      entityTitle: `Fault: ${fault.clientName || fault.symptom || faultId}`,
      createdBy: localStorage.getItem('user_id') || 'unknown',
      createdByName: authorName,
      tenantId: getTenantIdSync(),
      linkPath: `/home/faults?openId=${faultId}`,
      availableUsers,
      sourceNoteId,
    });
  } catch (err) {
    console.error('[fault.service] Mention fanout failed:', err);
  }
}

export async function addRemoteLog(
  faultId: string,
  author: string,
  action: RemoteLog['action'],
  notes: string
): Promise<string> {
  const now = new Date().toISOString();
  const faultRef = doc(db, 'faults', faultId);
  const faultSnap = await getDoc(faultRef);

  if (!faultSnap.exists()) {
    throw new Error('Fault not found');
  }

  const fault = faultSnap.data() as Fault;

  const newRemoteLog: RemoteLog = {
    id: crypto.randomUUID(),
    timestamp: now,
    author,
    action,
    notes,
  };

  const newHistoryEntry: InvestigationHistoryEntry = {
    id: crypto.randomUUID(),
    timestamp: now,
    stage: fault.stage,
    author,
    action: `Remote: ${action}`,
    details: notes,
  };

  // Check SLA response on first action
  const slaResponseUpdate: Record<string, unknown> = {};
  if (fault.slaResponseMet === undefined && fault.slaResponseDeadline) {
    const met = new Date(now).getTime() <= new Date(fault.slaResponseDeadline).getTime();
    slaResponseUpdate.slaResponseMet = met;
    if (!met) slaResponseUpdate.slaBreached = true;
  }

  await updateDoc(faultRef, {
    remoteLogs: [...fault.remoteLogs, newRemoteLog],
    investigationHistory: arrayUnion(newHistoryEntry),
    ...slaResponseUpdate,
    updatedAt: now,
  });

  void fanoutFaultNoteMentions(faultId, fault, notes, author, newRemoteLog.id);

  return newRemoteLog.id;
}

/**
 * Add a call note
 */
export async function addCallNote(
  faultId: string,
  author: string,
  notes: string,
  callDuration?: number
): Promise<string> {
  const now = new Date().toISOString();
  const faultRef = doc(db, 'faults', faultId);
  const faultSnap = await getDoc(faultRef);

  if (!faultSnap.exists()) {
    throw new Error('Fault not found');
  }

  const fault = faultSnap.data() as Fault;

  const newCallNote: CallNote = {
    id: crypto.randomUUID(),
    timestamp: now,
    author,
    notes,
    ...(callDuration !== undefined && { callDuration }),
  };

  const newHistoryEntry: InvestigationHistoryEntry = {
    id: crypto.randomUUID(),
    timestamp: now,
    stage: fault.stage,
    author,
    action: 'Call Logged',
    details: notes,
  };

  // Check SLA response on first action
  const slaResponseUpdate: Record<string, unknown> = {};
  if (fault.slaResponseMet === undefined && fault.slaResponseDeadline) {
    const met = new Date(now).getTime() <= new Date(fault.slaResponseDeadline).getTime();
    slaResponseUpdate.slaResponseMet = met;
    if (!met) slaResponseUpdate.slaBreached = true;
  }

  // Accumulate call duration into costs
  const costUpdate: Record<string, unknown> = {};
  if (callDuration && callDuration > 0) {
    const currentCosts: FaultCosts = fault.costs || {
      labourMinutes: 0, labourCost: 0, materials: [], materialsCost: 0, totalCost: 0,
    };
    costUpdate.costs = recalculateCosts({
      ...currentCosts,
      labourMinutes: currentCosts.labourMinutes + callDuration,
    });
  }

  await updateDoc(faultRef, {
    callNotes: [...fault.callNotes, newCallNote],
    investigationHistory: arrayUnion(newHistoryEntry),
    ...slaResponseUpdate,
    ...costUpdate,
    updatedAt: now,
  });

  void fanoutFaultNoteMentions(faultId, fault, notes, author, newCallNote.id);

  return newCallNote.id;
}

/**
 * Add a client communication update (outbound to customer)
 */
export async function addClientUpdate(
  faultId: string,
  author: string,
  method: ClientUpdate['method'],
  content: string,
  nextUpdateDue?: string
): Promise<void> {
  const now = new Date().toISOString();
  const faultRef = doc(db, 'faults', faultId);
  const faultSnap = await getDoc(faultRef);

  if (!faultSnap.exists()) {
    throw new Error('Fault not found');
  }

  const fault = faultSnap.data() as Fault;

  const methodLabels = { phone: 'Phone', sms: 'SMS', email: 'Email' };

  const newClientUpdate: ClientUpdate = {
    id: crypto.randomUUID(),
    timestamp: now,
    author,
    method,
    content,
    nextUpdateDue,
  };

  const newHistoryEntry: InvestigationHistoryEntry = {
    id: crypto.randomUUID(),
    timestamp: now,
    stage: fault.stage,
    author,
    action: `Client Update (${methodLabels[method]})`,
    details: content,
  };

  // Check SLA response on first action
  const slaResponseUpdate: Record<string, unknown> = {};
  if (fault.slaResponseMet === undefined && fault.slaResponseDeadline) {
    const met = new Date(now).getTime() <= new Date(fault.slaResponseDeadline).getTime();
    slaResponseUpdate.slaResponseMet = met;
    if (!met) slaResponseUpdate.slaBreached = true;
  }

  await updateDoc(faultRef, {
    clientUpdates: [...(fault.clientUpdates || []), newClientUpdate],
    investigationHistory: arrayUnion(newHistoryEntry),
    ...slaResponseUpdate,
    updatedAt: now,
  });

  void fanoutFaultNoteMentions(faultId, fault, content, author, newClientUpdate.id);
}

/**
 * Log a "Tagged to todo" entry on the fault's investigation timeline.
 *
 * Called when an office user creates a todo linked to this fault (via the
 * "Tag to Todo" action in the fault detail modal). The actual todo is
 * created in the `todos` collection by the caller — this function just
 * makes sure the fault timeline records who was tagged and why, so anyone
 * reviewing the fault later sees the trail.
 */
export async function logTagToTodoOnFault(
  faultId: string,
  author: string,
  assigneeNames: string[],
  note: string,
  dueDate?: string
): Promise<string | undefined> {
  if (!faultId || assigneeNames.length === 0) return undefined;

  const now = new Date().toISOString();
  const faultRef = doc(db, 'faults', faultId);
  const faultSnap = await getDoc(faultRef);

  if (!faultSnap.exists()) return undefined;

  const fault = faultSnap.data() as Fault;

  const assigneeList = assigneeNames.join(', ');
  const dueSuffix = dueDate
    ? ` (due ${new Date(dueDate + 'T00:00:00').toLocaleDateString(getLocaleSync(), { day: 'numeric', month: 'short' })})`
    : '';

  const newHistoryEntry: InvestigationHistoryEntry = {
    id: crypto.randomUUID(),
    timestamp: now,
    stage: fault.stage,
    author,
    action: `Tagged ${assigneeList} to todo${dueSuffix}`,
    details: note.trim(),
  };

  await updateDoc(faultRef, {
    investigationHistory: arrayUnion(newHistoryEntry),
    updatedAt: now,
  });

  return newHistoryEntry.id;
}

/**
 * Request a tech consult for a fault.
 * This triggers the notification bell for the assigned tech.
 *
 * NOTE (Jul 2026 redesign): this is a plain action — it no longer moves the
 * fault's stage. Stage moves are manual via the stage picker.
 */
export async function requestTechConsult(
  faultId: string,
  requestedBy: string,
  assignedTechId: string,
  reason: string,
  providedTechName?: string
): Promise<void> {
  const now = new Date().toISOString();
  const faultRef = doc(db, 'faults', faultId);
  const faultSnap = await getDoc(faultRef);

  if (!faultSnap.exists()) {
    throw new Error('Fault not found');
  }

  const fault = { id: faultId, ...faultSnap.data() } as Fault;

  // Resolve tech display name: caller-provided name → users doc → "Unknown Tech".
  // (The deprecated `team` collection read was removed — that doc never exists,
  // and under the rules lock a get on it would throw instead of returning empty.)
  let techName = providedTechName?.trim() || '';
  if (!techName) {
    const userSnap = await getDoc(doc(db, 'users', assignedTechId));
    if (userSnap.exists()) techName = userSnap.data().name;
  }
  if (!techName) techName = 'Unknown Tech';

  const newHistoryEntry: InvestigationHistoryEntry = {
    id: crypto.randomUUID(),
    timestamp: now,
    stage: migrateFaultStage(fault.stage),
    author: requestedBy,
    action: `${requestedBy} requested tech consult`,
    details: `Assigned to ${techName} for consultation. Reason: ${reason}`,
  };

  // Check SLA response on first action
  const slaResponseUpdate: Record<string, unknown> = {};
  if (fault.slaResponseMet === undefined && fault.slaResponseDeadline) {
    const met = new Date(now).getTime() <= new Date(fault.slaResponseDeadline).getTime();
    slaResponseUpdate.slaResponseMet = met;
    if (!met) slaResponseUpdate.slaBreached = true;
  }

  // Update fault document (no stage change — consult is a plain action)
  await updateDoc(faultRef, {
    consultRequested: true,
    consultRequestedAt: now,
    consultRequestedBy: requestedBy,
    assignedTechId,
    investigationHistory: arrayUnion(newHistoryEntry),
    ...slaResponseUpdate,
    updatedAt: now,
  });

  // Create consult request for tech dashboard
  const consultRequest: Record<string, unknown> = {
    faultId,
    assignedTechId,
    assignedTechName: techName,
    requestedBy,
    requestedByName: requestedBy,
    requestedAt: now,
    clientName: fault.clientName,
    symptom: fault.symptom,
    severity: fault.severity,
    address: fault.address,
    reason,
    status: 'PENDING',
    // REQUIRED by the rules lock — fault_consult_requests creates must carry the caller's tenant.
    tenantId: getTenantIdSync(),
  };

  // Only include deviceInfo if it exists (Firestore rejects undefined)
  if (fault.deviceInfo) {
    consultRequest.deviceInfo = fault.deviceInfo;
  }

  await addDoc(collection(db, 'fault_consult_requests'), consultRequest);
}

/**
 * Mark tech consult as complete
 */
export async function completeTechConsult(
  faultId: string,
  techId: string,
  consultNotes: string
): Promise<void> {
  const now = new Date().toISOString();
  const faultRef = doc(db, 'faults', faultId);
  const faultSnap = await getDoc(faultRef);

  if (!faultSnap.exists()) {
    throw new Error('Fault not found');
  }

  const fault = faultSnap.data() as Fault;

  const newHistoryEntry: InvestigationHistoryEntry = {
    id: crypto.randomUUID(),
    timestamp: now,
    stage: migrateFaultStage(fault.stage),
    author: techId,
    action: 'Tech Consult Completed',
    details: consultNotes,
  };

  await updateDoc(faultRef, {
    consultCompletedAt: now,
    consultNotes,
    investigationHistory: arrayUnion(newHistoryEntry),
    updatedAt: now,
  });
}

/**
 * CRITICAL: Stage 04 Handoff Logic
 *
 * This is the heart of the Fault Pipeline → Deployment Pipeline integration.
 *
 * When a fault is promoted to Stage 04:
 * 1. Archive the fault ticket
 * 2. Create a new entry in the Deployment Pipe (jobs collection)
 * 3. Pass ALL investigation history to the new job's briefingHistory field
 *
 * The tech will see this in their "Daily Jobs" view with full context.
 */
export async function handoffToDeployment(
  faultId: string,
  handoffBy: string,
  jobDetails: {
    scheduledDate: string; // YYYY-MM-DD (empty string = unscheduled)
    scheduledTime: string; // HH:MM (empty string = unscheduled)
    assignedTechIds: string[];
    estimatedDuration: number; // Minutes
    services?: string[];
    criticalNotes?: string;
    preferredTechId?: string;
    preferredTechName?: string;
    scheduleByDate?: string; // YYYY-MM-DD — when it needs to be scheduled by
    customerPreferredDate?: string; // YYYY-MM-DD — customer's preferred date
    deploymentNotes?: string;
  }
): Promise<string> {
  const now = new Date().toISOString();
  const faultRef = doc(db, 'faults', faultId);
  const faultSnap = await getDoc(faultRef);

  if (!faultSnap.exists()) {
    throw new Error('Fault not found');
  }

  const fault = faultSnap.data() as Fault;

  // Build the briefing history for the tech
  const briefingHistory: JobBriefingHistory = buildBriefingHistory(fault, faultId);

  // Determine if job is scheduled or not
  const isScheduled = jobDetails.scheduledDate && jobDetails.scheduledTime;

  // Auto-attach checklist template checkpoints for Fault jobs
  const faultCategory = (jobDetails.services?.[0]) ?? '';
  const templateCheckpoints = faultCategory
    ? await getCheckpointsForNewJob(getTenantIdSync(), faultCategory, 'Fault')
    : null;

  const deploymentCoords = await resolveFaultJobCoords(fault);

  // Create the deployment job (build it carefully to avoid undefined values)
  const newJob: Record<string, unknown> = {
    jobType: 'job',
    client: fault.clientName,
    type: 'Fault', // Mark as fault-related job
    status: isScheduled ? 'Scheduled' : 'Unscheduled',
    tenantId: fault.tenantId ?? getTenantIdSync(),

    // Location
    loc: addressSuburb({ address: fault.address }), // The town — `address` carries the whole thing
    address: fault.address,
    lat: deploymentCoords.lat,
    lng: deploymentCoords.lng,
    ...(deploymentCoords.formatted
      ? { geocodedAt: new Date().toISOString(), geocodedAddress: deploymentCoords.formatted }
      : {}),

    // Scheduling
    date: jobDetails.scheduledDate || '',
    time: jobDetails.scheduledTime || '',
    duration: jobDetails.estimatedDuration,

    // Assignment
    techIds: jobDetails.assignedTechIds || [],
    techId: jobDetails.assignedTechIds?.[0] || '', // Primary tech (empty if no techs assigned)

    // Services & Notes
    services: jobDetails.services || ['Fault Resolution'],
    notes: `FAULT HANDOFF: ${fault.symptom}`,

    // Customer link
    customerId: fault.customerId || null,

    // Fault Pipeline — bidirectional link
    faultId: faultId,

    // Fault service context — so tech knows what service is affected
    ...(fault.serviceType ? { faultServiceType: fault.serviceType } : {}),
    ...(fault.servicePlanName ? { faultServicePlanName: fault.servicePlanName } : {}),
    ...(fault.serviceId ? { faultServiceId: fault.serviceId } : {}),

    // CRITICAL: Pass the briefing history
    briefingHistory, // Will add to Job type

    // Offline copy of the field briefing. The tech card reads the LIVE fault
    // first (so an office edit after dispatch lands); this only has to survive
    // a tech opening the job with no signal.
    ...(fault.techBriefing ? { techBriefing: fault.techBriefing } : {}),

    // Checklist + auto-attached template checkpoints
    checklist: [],
    ...(templateCheckpoints ? { checkpoints: templateCheckpoints } : {}),

    // Fault severity for priority display
    priority: fault.severity === 'critical' ? 'Critical' : fault.severity === 'high' ? 'High' : 'Normal',

    // Lifecycle
    sos: fault.severity === 'critical', // Mark critical faults as SOS
    deleted: false,
    createdAt: now,
  };

  // Build scheduling preferences (only if any values provided)
  const hasPrefs = jobDetails.preferredTechId || jobDetails.scheduleByDate || jobDetails.customerPreferredDate || jobDetails.deploymentNotes;
  if (hasPrefs) {
    const prefs: Record<string, string> = {};
    if (jobDetails.preferredTechId) prefs.preferredTechId = jobDetails.preferredTechId;
    if (jobDetails.preferredTechName) prefs.preferredTechName = jobDetails.preferredTechName;
    if (jobDetails.scheduleByDate) prefs.scheduleByDate = jobDetails.scheduleByDate;
    if (jobDetails.customerPreferredDate) prefs.customerPreferredDate = jobDetails.customerPreferredDate;
    if (jobDetails.deploymentNotes) prefs.deploymentNotes = jobDetails.deploymentNotes;
    newJob.faultDeploymentPrefs = prefs;
  }

  // Only add criticalNotes if it exists (avoid undefined)
  if (jobDetails.criticalNotes) {
    newJob.criticalNotes = jobDetails.criticalNotes;
  }

  // Deep-strip undefined before the write. The top-level fields are built
  // carefully above, but the nested `briefingHistory` is copied straight off
  // the fault — and its `timeline[]` carries `stage` / `author` / `timestamp`
  // from `investigationHistory` entries, which can be stage-less / action-less
  // for faults whose linked job was edited (see PR #1267). Firestore rejects
  // any nested `undefined`, so a careful top-level build isn't enough.
  const jobRef = await addDoc(collection(db, 'jobs'), stripUndefinedDeep(newJob));
  const jobId = jobRef.id;

  // Update fault: Archive and link to deployment job
  const handoffHistoryEntry: InvestigationHistoryEntry = {
    id: crypto.randomUUID(),
    timestamp: now,
    stage: '03-Deploy',
    author: handoffBy,
    action: 'Deployed',
    details: `Fault resolved via deployment. Job created: ${jobId}`,
  };

  // Set follow-up due date (5 days after deployment)
  const followUpDueDate = new Date();
  followUpDueDate.setDate(followUpDueDate.getDate() + 5);

  // Check SLA resolution
  const slaResolutionUpdate: Record<string, unknown> = {};
  if (fault.slaResolutionDeadline) {
    const met = new Date(now).getTime() <= new Date(fault.slaResolutionDeadline).getTime();
    slaResolutionUpdate.slaResolutionMet = met;
    if (!met) slaResolutionUpdate.slaBreached = true;
  }

  await updateDoc(faultRef, {
    stage: '03-Deploy',
    resolutionType: 'deployed',
    resolvedAt: now,
    resolvedBy: handoffBy,
    resolutionNotes: `Deployment job created: ${jobId}`,
    archived: true,
    archivedAt: now,
    deploymentJobId: jobId,
    followUpDueDate: followUpDueDate.toISOString(),
    followUpCompleted: false,
    investigationHistory: arrayUnion(handoffHistoryEntry),
    ...slaResolutionUpdate,
    updatedAt: now,
  });

  // Fire-and-forget: Decrement open fault count on linked service
  if (fault.serviceId) {
    decrementOpenFaultCount(fault.serviceId).catch((err: unknown) =>
      console.error('[FaultServiceSync] Decrement failed:', err)
    );
  }

  return jobId;
}

/**
 * Send fault to provisioning queue
 * Marks fault as needing provisioning before deployment
 */
export async function sendToProvisioning(
  faultId: string,
  sentBy: string,
  provisioningDetails: {
    assignedTechIds: string[];
    notes?: string;
  }
): Promise<void> {
  const now = new Date().toISOString();
  const faultRef = doc(db, 'faults', faultId);
  const faultSnap = await getDoc(faultRef);

  if (!faultSnap.exists()) {
    throw new Error('Fault not found');
  }

  const fault = faultSnap.data() as Fault;

  // Add history entry
  const provisioningHistoryEntry: InvestigationHistoryEntry = {
    id: crypto.randomUUID(),
    timestamp: now,
    stage: fault.stage,
    author: sentBy,
    action: 'Sent to Provisioning',
    details: `Fault sent to provisioning for parts/equipment preparation. Assigned techs: ${provisioningDetails.assignedTechIds.length}`,
  };

  // Update fault with provisioning status
  await updateDoc(faultRef, {
    needsProvisioning: true,
    provisioningStatus: 'pending',
    provisioningAssignedTechIds: provisioningDetails.assignedTechIds,
    provisioningNotes: provisioningDetails.notes || '',
    provisioningSentAt: now,
    provisioningSentBy: sentBy,
    investigationHistory: arrayUnion(provisioningHistoryEntry),
    updatedAt: now,
  });
}

/**
 * Clear a fault out of the provisioning queue.
 *
 * Used when a fault was sent to provisioning but no line items were ever added
 * (e.g. parts were sourced verbally / on the fly), or when ops just wants to
 * remove it from the provisioning view. The fault itself is untouched —
 * stage, archive, deployment job links etc. all remain.
 *
 * @param outcome - 'completed' marks provisioning done; 'cancelled' clears
 *                  it as if it never went to provisioning.
 */
export async function clearFaultFromProvisioning(
  faultId: string,
  clearedBy: string,
  outcome: 'completed' | 'cancelled' = 'completed',
): Promise<void> {
  const now = new Date().toISOString();
  const faultRef = doc(db, 'faults', faultId);
  const faultSnap = await getDoc(faultRef);
  if (!faultSnap.exists()) throw new Error('Fault not found');

  const fault = faultSnap.data() as Fault;

  const action = outcome === 'completed' ? 'Provisioning Complete' : 'Removed from Provisioning';
  const details =
    outcome === 'completed'
      ? 'Marked provisioning as complete — fault cleared from provisioning queue.'
      : 'Removed from provisioning queue without ordering — handled outside the system.';

  const historyEntry: InvestigationHistoryEntry = {
    id: crypto.randomUUID(),
    timestamp: now,
    stage: fault.stage,
    author: clearedBy,
    action,
    details,
  };

  await updateDoc(faultRef, {
    needsProvisioning: false,
    provisioningStatus: 'completed',
    provisioningCompletedAt: now,
    provisioningCompletedBy: clearedBy,
    investigationHistory: arrayUnion(historyEntry),
    updatedAt: now,
  });
}

/**
 * Restore a fault back to the provisioning queue (undo clearFaultFromProvisioning).
 * Reverses the cleared/completed state so the fault reappears in the
 * /home/provisioning queue. Existing line items, investigation history, and
 * stage are untouched.
 */
export async function restoreFaultToProvisioning(
  faultId: string,
  restoredBy: string,
): Promise<void> {
  const now = new Date().toISOString();
  const faultRef = doc(db, 'faults', faultId);
  const faultSnap = await getDoc(faultRef);
  if (!faultSnap.exists()) throw new Error('Fault not found');

  const fault = faultSnap.data() as Fault;

  const historyEntry: InvestigationHistoryEntry = {
    id: crypto.randomUUID(),
    timestamp: now,
    stage: fault.stage,
    author: restoredBy,
    action: 'Reopened in Provisioning',
    details: 'Restored to the provisioning queue from the recently-cleared list.',
  };

  await updateDoc(faultRef, {
    needsProvisioning: true,
    provisioningStatus: 'pending',
    provisioningCompletedAt: null,
    provisioningCompletedBy: null,
    investigationHistory: arrayUnion(historyEntry),
    updatedAt: now,
  });
}

/**
 * Build comprehensive briefing history from fault data
 * This is what the tech sees in their job view
 */
function buildBriefingHistory(fault: Fault, faultId: string): JobBriefingHistory {
  // Consolidate all timeline entries
  const timeline: JobBriefingHistory['timeline'] = [];

  // Add call notes
  fault.callNotes.forEach((note) => {
    timeline.push({
      timestamp: note.timestamp,
      stage: 'Phone Call',
      author: note.author,
      type: 'call',
      content: note.notes,
    });
  });

  // Add remote logs
  fault.remoteLogs.forEach((log) => {
    timeline.push({
      timestamp: log.timestamp,
      stage: 'Remote Investigation',
      author: log.author,
      type: 'diagnostic',
      content: `${log.action}: ${log.notes}`,
    });
  });

  // Add client updates
  (fault.clientUpdates || []).forEach((update) => {
    timeline.push({
      timestamp: update.timestamp,
      stage: 'Client Communication',
      author: update.author,
      type: 'client_update',
      content: `[${update.method.toUpperCase()}] ${update.content}`,
    });
  });

  // Add investigation history (guard: legacy/edge fault docs may lack it)
  (fault.investigationHistory || []).forEach((entry) => {
    timeline.push({
      timestamp: entry.timestamp,
      stage: entry.stage,
      author: entry.author,
      type: 'investigation',
      content: `${entry.action} - ${entry.details}`,
    });
  });

  // Sort by timestamp
  timeline.sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );

  // Generate summary of remote diagnostics — check ALL diagnosis sources,
  // not just remoteLogs, since office-side diagnosis may be logged as call
  // notes, investigation history entries, or tech consult responses.
  const diagnosisParts: string[] = [];

  if (fault.remoteLogs.length > 0) {
    diagnosisParts.push(
      fault.remoteLogs.map((log) => `${log.action}: ${log.notes}`).join(' | ')
    );
  }

  if (fault.callNotes.length > 0) {
    diagnosisParts.push(
      `${fault.callNotes.length} call note${fault.callNotes.length > 1 ? 's' : ''} logged`
    );
  }

  const diagnosisHistoryEntries = (fault.investigationHistory || []).filter(
    (e) =>
      /diagnostic|diagnos|investigat|remote|reboot|config/i.test(e.action) ||
      /diagnostic|diagnos|investigat|remote|reboot|config/i.test(e.details)
  );
  if (diagnosisHistoryEntries.length > 0) {
    diagnosisParts.push(
      diagnosisHistoryEntries.map((e) => e.action).join(' | ')
    );
  }

  if (fault.lastConsultResponse) {
    diagnosisParts.push(
      `Tech consult: ${fault.lastConsultResponse.recommendation}${fault.lastConsultResponse.notes ? ` — ${fault.lastConsultResponse.notes.slice(0, 80)}` : ''}`
    );
  }

  const remoteDiagnosticsSummary =
    diagnosisParts.length > 0
      ? diagnosisParts.join(' · ')
      : 'No remote diagnostics performed';

  const result: JobBriefingHistory = {
    source: 'fault_pipeline',
    faultId: faultId,
    originalIntakeDate: fault.intakeDate,
    timeline,
    symptom: fault.symptom,
    severity: fault.severity,
    remoteDiagnosticsSummary,
  };

  if (fault.deviceInfo) {
    result.deviceInfo = fault.deviceInfo;
  }
  if (fault.serviceType) {
    result.serviceType = fault.serviceType;
  }
  if (fault.servicePlanName) {
    result.servicePlanName = fault.servicePlanName;
  }

  return result;
}

/**
 * Hard-delete a fault — for records raised in error / duplicates the office
 * just wants gone, with no completion ceremony. Decrements the linked
 * service's open-fault counter (best-effort) so counts stay honest. Linked
 * jobs are left untouched (they carry their own history).
 */
export async function deleteFault(faultId: string): Promise<void> {
  const faultRef = doc(db, 'faults', faultId);
  const snap = await getDoc(faultRef);
  if (!snap.exists()) return;
  const data = snap.data() as Fault;

  await deleteDoc(faultRef);

  // Keep the linked service's open-fault count honest — mirror of the
  // decrement closeResolveFault does. Best-effort; the fault is already gone.
  if (data.serviceId && !data.archived) {
    updateDoc(doc(db, 'customer_services', data.serviceId), {
      openFaultCount: increment(-1),
      updatedAt: new Date().toISOString(),
    }).catch((err) => console.warn('[deleteFault] open-fault decrement failed:', err));
  }

  console.log('🗑️ Fault deleted:', faultId);
}

/**
 * Close/Resolve fault without deployment
 */
export async function closeResolveFault(
  faultId: string,
  resolvedBy: string,
  resolutionType: 'resolved_remotely' | 'closed_duplicate' | 'closed_no_fault_found',
  resolutionNotes: string
): Promise<void> {
  const now = new Date().toISOString();
  const faultRef = doc(db, 'faults', faultId);
  const faultSnap = await getDoc(faultRef);

  if (!faultSnap.exists()) {
    throw new Error('Fault not found');
  }

  const fault = faultSnap.data() as Fault;

  const resolutionLabels = {
    resolved_remotely: 'Resolved Remotely',
    closed_duplicate: 'Closed - Duplicate',
    closed_no_fault_found: 'Closed - No Fault Found',
  };

  const resolutionHistoryEntry: InvestigationHistoryEntry = {
    id: crypto.randomUUID(),
    timestamp: now,
    stage: '04-Review',
    author: resolvedBy,
    action: resolutionLabels[resolutionType],
    details: resolutionNotes,
  };

  // Set follow-up due date (5 days after resolution)
  const followUpDueDate = new Date();
  followUpDueDate.setDate(followUpDueDate.getDate() + 5);

  // Check SLA resolution
  const slaResolutionUpdate: Record<string, unknown> = {};
  if (fault.slaResolutionDeadline) {
    const met = new Date(now).getTime() <= new Date(fault.slaResolutionDeadline).getTime();
    slaResolutionUpdate.slaResolutionMet = met;
    if (!met) slaResolutionUpdate.slaBreached = true;
  }

  await updateDoc(faultRef, {
    stage: '04-Review',
    resolutionType,
    resolvedAt: now,
    resolvedBy,
    resolutionNotes,
    archived: true,
    archivedAt: now,
    followUpDueDate: followUpDueDate.toISOString(),
    followUpCompleted: false,
    investigationHistory: arrayUnion(resolutionHistoryEntry),
    ...slaResolutionUpdate,
    updatedAt: now,
  });

  // Fire-and-forget: Decrement open fault count on linked service
  if (fault.serviceId) {
    decrementOpenFaultCount(fault.serviceId).catch((err: unknown) =>
      console.error('[FaultServiceSync] Decrement failed:', err)
    );
  }

  // Customer 360 audit log — fire-and-forget. Day 5.
  if (fault.customerId) {
    logCustomerActivity({
      customerId: fault.customerId,
      actorId: resolvedBy,
      actorName: resolveActorName(resolvedBy),
      entryType: 'stage_change',
      summary: `Fault closed — ${resolutionLabels[resolutionType]}`,
      sourceEntity: 'fault',
      sourceId: faultId,
      timestamp: now,
    }).catch((err) => console.error('[closeResolveFault] customer audit log failed:', err));
  }
}

/**
 * Start 48-hour monitoring phase after job completion
 */
export async function startMonitoringPhase(faultId: string): Promise<void> {
  const now = new Date();
  const expiresAt = new Date(now.getTime() + 48 * 60 * 60 * 1000); // 48 hours

  const faultRef = doc(db, 'faults', faultId);

  await updateDoc(faultRef, {
    monitoringPhase: {
      active: true,
      startedAt: now.toISOString(),
      expiresAt: expiresAt.toISOString(),
      alertsSent: 0,
    },
    updatedAt: now.toISOString(),
  });
}

/**
 * Check if fault is in monitoring phase and alert if device offline
 */
export async function checkMonitoringPhase(
  faultId: string,
  deviceOffline: boolean
): Promise<boolean> {
  const faultRef = doc(db, 'faults', faultId);
  const faultSnap = await getDoc(faultRef);

  if (!faultSnap.exists()) {
    return false;
  }

  const fault = faultSnap.data() as Fault;

  if (!fault.monitoringPhase?.active) {
    return false;
  }

  const now = new Date();
  const expiresAt = new Date(fault.monitoringPhase.expiresAt);

  // Check if monitoring period expired
  if (now > expiresAt) {
    await updateDoc(faultRef, {
      'monitoringPhase.active': false,
      updatedAt: now.toISOString(),
    });
    return false;
  }

  // If device is offline during monitoring, trigger alert
  if (deviceOffline) {
    await updateDoc(faultRef, {
      'monitoringPhase.alertsSent': fault.monitoringPhase.alertsSent + 1,
      updatedAt: now.toISOString(),
    });
    return true; // Alert should be sent
  }

  return false;
}

/**
 * Complete follow-up for a resolved fault
 */
export async function completeFollowUp(
  faultId: string,
  _completedBy: string,
  followUpNotes: string
): Promise<void> {
  const now = new Date().toISOString();
  const faultRef = doc(db, 'faults', faultId);

  await updateDoc(faultRef, {
    followUpCompleted: true,
    followUpCompletedAt: now,
    followUpNotes,
    updatedAt: now,
  });
}

/**
 * Add a material cost entry to a fault
 */
export async function addFaultMaterial(
  faultId: string,
  material: FaultMaterialItem
): Promise<void> {
  const now = new Date().toISOString();
  const faultRef = doc(db, 'faults', faultId);
  const faultSnap = await getDoc(faultRef);

  if (!faultSnap.exists()) {
    throw new Error('Fault not found');
  }

  const fault = faultSnap.data() as Fault;
  const currentCosts: FaultCosts = fault.costs || {
    labourMinutes: 0, labourCost: 0, materials: [], materialsCost: 0, totalCost: 0,
  };

  const updatedCosts = recalculateCosts({
    ...currentCosts,
    materials: [...currentCosts.materials, material],
  });

  await updateDoc(faultRef, {
    costs: updatedCosts,
    updatedAt: now,
  });
}

/**
 * Freeform line item input for faults — everything ex-GST, as entered by the user.
 * Mirrors the deal workspace `FreeformLineDraft` shape.
 */
export interface FaultFreeformLineDraft {
  name: string;
  quantity: number;
  unitPrice: number;  // ex-GST
  totalPrice: number; // ex-GST (quantity × unitPrice)
  billingType: 'one-time' | 'recurring';
  sendToScheduling: boolean;
  /** Set when the line was picked from the Parts Hub catalog */
  partsCatalogId?: string;
}

/**
 * Add freeform line items to a fault.
 *
 * Mirror of the deal-side `addFreeformLineItems()` — lets office staff tack on
 * ad-hoc items (parts, extra labour, callout fees) while a fault is in or
 * about to enter provisioning, without going through the catalog.
 *
 * For each line we:
 *   - Append a FaultMaterialItem to `fault.costs.materials` (one-time lines
 *     only — keeps labour/materials split clean for billing).
 *   - Create a provisioning_items doc if `sendToScheduling` is true, so the
 *     part appears on /home/provisioning with a faultId/sourceType: 'fault'.
 *   - Flip `needsProvisioning` / `provisioningStatus: 'pending'` on the fault
 *     if any line was sent to provisioning and it isn't already flagged.
 *
 * A single investigation history entry summarises the batch.
 */
export async function addFreeformLineItemsToFault(
  faultId: string,
  lines: FaultFreeformLineDraft[],
  userId: string,
  userName: string
): Promise<void> {
  if (lines.length === 0) return;

  const now = new Date().toISOString();
  const faultRef = doc(db, 'faults', faultId);
  const faultSnap = await getDoc(faultRef);
  if (!faultSnap.exists()) {
    throw new Error('Fault not found');
  }
  const fault = faultSnap.data() as Fault;

  // Append one-time items to fault.costs.materials for billing/reporting
  const newMaterials: FaultMaterialItem[] = lines
    .filter((l) => l.billingType === 'one-time')
    .map((l) => ({
      description: l.name,
      quantity: l.quantity,
      unitCost: l.unitPrice,
      ...(l.partsCatalogId ? { partsCatalogId: l.partsCatalogId } : {}),
    }));

  const currentCosts: FaultCosts = fault.costs || {
    labourMinutes: 0, labourCost: 0, materials: [], materialsCost: 0, totalCost: 0,
  };
  const updatedCosts = recalculateCosts({
    ...currentCosts,
    materials: [...currentCosts.materials, ...newMaterials],
  });

  // Create provisioning_items for lines flagged "send to scheduling/provisioning"
  const scheduledLines = lines.filter((l) => l.sendToScheduling);
  for (const line of scheduledLines) {
    try {
      const itemDoc: Record<string, unknown> = {
        // tenantId required by Phase 0.5 Session A rules on /provisioning_items.
        // Without this, the rule rejects the write — and historically (before
        // rules tightened) writes silently succeeded as orphan docs without
        // tenantId, breaking the audit / multi-tenant story downstream.
        tenantId: getTenantIdSync(),
        dealId: '', // No deal — fault-sourced
        faultId,
        sourceType: 'fault',
        itemName: line.name,
        quantity: line.quantity,
        status: 'NOT_ORDERED',
        billingType: line.billingType,
        metadata: {
          unitPrice: line.unitPrice,
          unitCost: line.unitPrice,
          source: 'manual_freeform',
          customerName: fault.clientName,
          severity: fault.severity,
          serviceType: fault.serviceType || null,
        },
        createdAt: now,
        updatedAt: now,
      };
      await addDoc(collection(db, 'provisioning_items'), itemDoc);
    } catch (err) {
      console.error(`[FaultAddLineItems] Failed to create provisioning item for "${line.name}":`, err);
      // Continue with remaining lines — don't let one failure block the rest
    }
  }

  // History entry — single summary for the batch
  const oneTimeCount = lines.filter((l) => l.billingType === 'one-time').length;
  const recurringCount = lines.filter((l) => l.billingType === 'recurring').length;
  const parts: string[] = [`${lines.length} line${lines.length === 1 ? '' : 's'}`];
  if (oneTimeCount > 0) parts.push(`${oneTimeCount} one-time`);
  if (recurringCount > 0) parts.push(`${recurringCount} recurring`);
  if (scheduledLines.length > 0) parts.push(`${scheduledLines.length} sent to provisioning`);

  const historyEntry: InvestigationHistoryEntry = {
    id: crypto.randomUUID(),
    timestamp: now,
    stage: fault.stage,
    author: userName,
    action: 'Line items added manually',
    details: parts.join(' · '),
  };

  // If any lines went to provisioning, flip the provisioning flags so the
  // fault card reflects the provisioning state and the fault appears with
  // its items on /home/provisioning.
  const shouldFlipProvisioning =
    scheduledLines.length > 0 && !fault.needsProvisioning;

  const update: Record<string, unknown> = {
    costs: updatedCosts,
    investigationHistory: arrayUnion(historyEntry),
    updatedAt: now,
  };
  if (shouldFlipProvisioning) {
    update.needsProvisioning = true;
    update.provisioningStatus = 'pending';
    update.provisioningSentAt = now;
    update.provisioningSentBy = userId;
  }

  await updateDoc(faultRef, update);
}

/**
 * Sync job completion costs back to the originating fault.
 * Called when a deployment job is completed — pulls the on-site duration
 * back into the fault's labour costs.
 */
export async function syncJobCompletionCosts(
  faultId: string,
  jobDurationMinutes: number
): Promise<void> {
  const now = new Date().toISOString();
  const faultRef = doc(db, 'faults', faultId);
  const faultSnap = await getDoc(faultRef);

  if (!faultSnap.exists()) return;

  const fault = faultSnap.data() as Fault;
  const currentCosts: FaultCosts = fault.costs || {
    labourMinutes: 0, labourCost: 0, materials: [], materialsCost: 0, totalCost: 0,
  };

  const updatedCosts = recalculateCosts({
    ...currentCosts,
    labourMinutes: currentCosts.labourMinutes + jobDurationMinutes,
  });

  await updateDoc(faultRef, {
    costs: updatedCosts,
    updatedAt: now,
  });
}

/**
 * Accumulate time spent viewing/working on a fault ticket.
 * Called when the FaultDetailModal closes — adds elapsed minutes to labour costs
 * and keeps ONE consolidated "Ticket Reviewed" entry PER AUTHOR on the
 * timeline (updated cumulatively) so the timeline doesn't get flooded with
 * a dozen 14-second rows every time someone scrolls past a fault.
 * Minimum threshold: 10 seconds (ignore accidental opens).
 */
export async function accumulateViewTime(
  faultId: string,
  author: string,
  elapsedSeconds: number
): Promise<void> {
  // Ignore very short views (< 10 seconds — accidental clicks)
  if (elapsedSeconds < 10) return;

  const now = new Date().toISOString();
  const faultRef = doc(db, 'faults', faultId);
  const faultSnap = await getDoc(faultRef);

  if (!faultSnap.exists()) return;

  const fault = faultSnap.data() as Fault;

  const elapsedMinutes = Math.ceil(elapsedSeconds / 60);

  const currentCosts: FaultCosts = fault.costs || {
    labourMinutes: 0, labourCost: 0, materials: [], materialsCost: 0, totalCost: 0,
  };

  const updatedCosts = recalculateCosts({
    ...currentCosts,
    labourMinutes: currentCosts.labourMinutes + elapsedMinutes,
  });

  // Find the most recent existing "Ticket Reviewed" entry by this author and
  // roll the new time into it. We parse the cumulative seconds out of the
  // existing details string so we don't need a schema change — legacy entries
  // without the marker just fall back to the new seconds count.
  const existingHistory = fault.investigationHistory || [];
  const reviewEntryIndex = existingHistory.findIndex(
    (e) => e.action === 'Ticket Reviewed' && e.author === author
  );

  const CUMULATIVE_MARKER_RE = /\[cum:(\d+)s\]/;
  const prevCumulativeSeconds =
    reviewEntryIndex >= 0
      ? (() => {
          const m = (existingHistory[reviewEntryIndex].details || '').match(CUMULATIVE_MARKER_RE);
          return m ? parseInt(m[1], 10) : 0;
        })()
      : 0;

  const totalSeconds = prevCumulativeSeconds + elapsedSeconds;
  const totalMinutes = Math.floor(totalSeconds / 60);
  const totalSecondsRemainder = totalSeconds % 60;
  const totalLabel =
    totalMinutes >= 60
      ? `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m`
      : totalMinutes >= 1
        ? `${totalMinutes}m ${totalSecondsRemainder}s`
        : `${totalSecondsRemainder}s`;

  const details = `Viewed/worked on fault ticket for ${totalLabel} total [cum:${totalSeconds}s]`;

  let updatedHistory: InvestigationHistoryEntry[];
  if (reviewEntryIndex >= 0) {
    // Update the existing entry in place (keep the same id so it's stable)
    updatedHistory = [...existingHistory];
    updatedHistory[reviewEntryIndex] = {
      ...updatedHistory[reviewEntryIndex],
      timestamp: now,
      stage: fault.stage,
      details,
    };
  } else {
    const newHistoryEntry: InvestigationHistoryEntry = {
      id: crypto.randomUUID(),
      timestamp: now,
      stage: fault.stage,
      author,
      action: 'Ticket Reviewed',
      details,
    };
    updatedHistory = [...existingHistory, newHistoryEntry];
  }

  // Check SLA response on first action
  const slaResponseUpdate: Record<string, unknown> = {};
  if (fault.slaResponseMet === undefined && fault.slaResponseDeadline) {
    const met = new Date(now).getTime() <= new Date(fault.slaResponseDeadline).getTime();
    slaResponseUpdate.slaResponseMet = met;
    if (!met) slaResponseUpdate.slaBreached = true;
  }

  await updateDoc(faultRef, {
    costs: updatedCosts,
    investigationHistory: updatedHistory,
    ...slaResponseUpdate,
    updatedAt: now,
  });
}

/**
 * Get all open faults (not archived)
 */
export async function getOpenFaults(): Promise<Fault[]> {
  const q = query(
    collection(db, 'faults'),
    where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
    where('archived', '==', false),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Fault[];
}

// NOTE: getFaultsByStage / getTechConsultRequests were removed in the Jul 2026
// stage redesign — both were dead code (zero callers; the live consult path
// queries fault_consult_requests by status via useFaultConsultRequests), and
// stage-value Firestore queries can't see legacy stage strings. Filter
// client-side through migrateFaultStage instead.

/**
 * Sync fault stats to the linked customer_service document.
 * Called fire-and-forget on fault creation.
 */
async function syncFaultStatsToService(
  serviceId: string,
  symptom: string,
  timestamp: string
): Promise<void> {
  const serviceRef = doc(db, 'customer_services', serviceId);
  await updateDoc(serviceRef, {
    faultCount: increment(1),
    openFaultCount: increment(1),
    lastFaultAt: timestamp,
    lastFaultSymptom: symptom,
    updatedAt: timestamp,
  });
}

/**
 * Decrement open fault count on the linked service when a fault is resolved.
 * Called fire-and-forget on fault resolution/close.
 */
async function decrementOpenFaultCount(serviceId: string): Promise<void> {
  const serviceRef = doc(db, 'customer_services', serviceId);
  await updateDoc(serviceRef, {
    openFaultCount: increment(-1),
    updatedAt: new Date().toISOString(),
  });
}

/**
 * Re-raise a fault from a previously resolved/completed fault.
 * Creates a new fault pre-populated with the original's customer, address,
 * device, and service context. Links the two faults via relatedFaults.
 */
export async function reRaiseFault(
  originalFault: Fault,
  reRaisedBy: string,
  notes?: string
): Promise<string> {
  const now = new Date().toISOString();
  const nowMs = new Date(now).getTime();

  // Calculate SLA deadlines based on original severity
  const slaTarget = SLA_TARGETS[originalFault.severity];
  const slaResponseDeadline = new Date(nowMs + slaTarget.responseMinutes * 60 * 1000).toISOString();
  const slaResolutionDeadline = new Date(nowMs + slaTarget.resolutionHours * 60 * 60 * 1000).toISOString();

  const reRaiseNote = notes
    ? `Re-raised from fault ${originalFault.id}. ${notes}`
    : `Re-raised from fault ${originalFault.id}. Original symptom: ${originalFault.symptom}`;

  const newFault: Record<string, unknown> = {
    // Tenant isolation — MUST be set or the new fault is invisible to every
    // tenant-scoped read (useFaults' `where('tenantId','in',...)` filter
    // excludes docs with no tenantId), so re-raised faults never appear in
    // /faults. Mirror createFault().
    tenantId: getTenantIdSync(),

    // Customer & Location — carry over from original
    customerId: originalFault.customerId,
    clientName: originalFault.clientName,
    address: originalFault.address,

    // Service context
    ...(originalFault.serviceId ? { serviceId: originalFault.serviceId } : {}),
    ...(originalFault.serviceName ? { serviceName: originalFault.serviceName } : {}),
    ...(originalFault.serviceType ? { serviceType: originalFault.serviceType } : {}),
    ...(originalFault.servicePlanName ? { servicePlanName: originalFault.servicePlanName } : {}),
    ...(originalFault.serviceMonthlyPrice ? { serviceMonthlyPrice: originalFault.serviceMonthlyPrice } : {}),
    ...(originalFault.serviceActivatedAt ? { serviceActivatedAt: originalFault.serviceActivatedAt } : {}),

    // Device info
    ...(originalFault.deviceInfo ? { deviceInfo: originalFault.deviceInfo } : {}),

    // Coordinates
    ...(originalFault.lat ? { lat: originalFault.lat } : {}),
    ...(originalFault.lng ? { lng: originalFault.lng } : {}),

    // New intake data
    stage: '01-Triage' as FaultStage,
    intakeMethod: 'Manual' as const,
    intakeNotes: reRaiseNote,
    intakeDate: now,
    intakeBy: reRaisedBy,
    symptom: originalFault.symptom,
    severity: originalFault.severity,

    // Empty activity arrays
    remoteLogs: [],
    callNotes: [],
    clientUpdates: [],
    investigationHistory: [
      {
        id: crypto.randomUUID(),
        timestamp: now,
        stage: '01-Triage',
        author: reRaisedBy,
        action: 'Fault Re-raised',
        details: reRaiseNote,
      },
    ],

    // Link to original fault
    relatedFaults: [
      {
        faultId: originalFault.id!,
        symptom: originalFault.symptom,
        createdAt: originalFault.createdAt,
        resolutionType: originalFault.resolutionType || '',
      },
    ],

    consultRequested: false,
    archived: false,
    slaResponseDeadline,
    slaResolutionDeadline,
    costs: recalculateCosts({
      labourMinutes: 0,
      labourCost: 0,
      materials: [],
      materialsCost: 0,
      totalCost: 0,
    }),
    createdAt: now,
    updatedAt: now,
  };

  // A re-raise is a new ticket, so it gets its own FLT number.
  const { id: newFaultId } = await createTicketWithNumber('fault', getTenantIdSync(), newFault);

  // Fire-and-forget: run repeat detection on the new fault
  detectRepeatFaults(newFaultId, {
    customerId: originalFault.customerId,
    symptom: originalFault.symptom,
    severity: originalFault.severity,
    serviceId: originalFault.serviceId,
    deviceInfo: originalFault.deviceInfo,
    intakeBy: reRaisedBy,
  }, now).catch((err: unknown) =>
    console.error('[RepeatDetection] Re-raise detection failed:', err)
  );

  // Fire-and-forget: sync fault stats to linked service
  if (originalFault.serviceId) {
    syncFaultStatsToService(originalFault.serviceId, originalFault.symptom, now).catch((err: unknown) =>
      console.error('[FaultServiceSync] Re-raise sync failed:', err)
    );
  }

  return newFaultId;
}

/**
 * Get all faults linked to a specific customer service.
 * Returns newest-first for display in service history.
 */
export async function getFaultsForService(serviceId: string): Promise<Fault[]> {
  const q = query(
    collection(db, 'faults'),
    where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
    where('serviceId', '==', serviceId),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({
    id: d.id,
    ...d.data(),
  })) as Fault[];
}

/**
 * Get all faults for a given customer (by customerId).
 * Used for the "Previous History" section in FaultDetailModal.
 */
export async function getFaultsForCustomer(customerId: string): Promise<Fault[]> {
  const q = query(
    collection(db, 'faults'),
    where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
    where('customerId', '==', customerId),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({
    id: d.id,
    ...d.data(),
  })) as Fault[];
}

/**
 * Sync deployment job completion data back to the originating fault.
 * Called fire-and-forget from job-state.service when a Fault-type job completes.
 *
 * This updates the fault so it moves from the "Deployed" section
 * to the "Completed" section in the fault pipeline page.
 */
export async function syncJobCompletionToFault(
  faultId: string,
  completedJob: Job & { quickRaised?: boolean },
  techId: string
): Promise<void> {
  const now = new Date().toISOString();
  const faultRef = doc(db, 'faults', faultId);
  const faultSnap = await getDoc(faultRef);

  if (!faultSnap.exists()) {
    console.warn(`[FaultSync] Fault ${faultId} not found — skipping completion sync`);
    return;
  }

  const fault = faultSnap.data() as Fault;

  // Get tech name
  let techName = techId;
  try {
    const userRef = doc(db, 'users', techId);
    const userSnap = await getDoc(userRef);
    if (userSnap.exists()) {
      techName = userSnap.data().name || techId;
    }
  } catch {
    // Fall back to techId
  }

  const isReturnVisit = completedJob.needsReturnVisit === true;

  // Build deployment summary from the completed job
  const fieldLogEntries = (completedJob.fieldLog || [])
    .sort((a: FieldLogEntry, b: FieldLogEntry) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
    .map((entry: FieldLogEntry) => ({
      timestamp: entry.timestamp,
      text: entry.text,
      techName: entry.techName || undefined,
      // Preserve the diagnostic classification (observation/hypothesis/action/
      // result/ruled_out/decision) so the fault record keeps the tech's
      // reasoning structure, not just the raw text.
      ...(entry.entryType ? { entryType: entry.entryType } : {}),
    }));

  const deploymentJobSummary: Record<string, unknown> = {
    techName,
    actualDuration: completedJob.actualDuration || completedJob.duration || 0,
    fieldLogCount: fieldLogEntries.length,
    fieldLogEntries,
    photoCount: (completedJob.photos || []).length,
    needsReturnVisit: isReturnVisit,
  };

  // Include photo URLs so they're accessible from the fault record
  if (completedJob.photos && completedJob.photos.length > 0) {
    deploymentJobSummary.photos = completedJob.photos.map((p: JobPhoto & { takenAt?: string }) => ({
      url: p.url,
      caption: p.caption || '',
      takenAt: p.timestamp || p.takenAt,
      takenBy: p.techName || p.techId,
    }));
  }

  if (completedJob.completionNote) {
    deploymentJobSummary.completionNote = completedJob.completionNote;
  }
  if (completedJob.additionalGearUsed && completedJob.additionalGearUsed.length > 0) {
    deploymentJobSummary.additionalGearUsed = completedJob.additionalGearUsed;
  }
  if (completedJob.usedMaterials && completedJob.usedMaterials.length > 0) {
    deploymentJobSummary.usedMaterials = completedJob.usedMaterials;
  }
  if (isReturnVisit && completedJob.returnVisitReason) {
    deploymentJobSummary.returnVisitReason = completedJob.returnVisitReason;
  }
  // Tech's completion reasoning ("your read on this job") — previously captured
  // on the job but never synced, so the fault record lost the diagnosis.
  if (completedJob.completionReasoning && !completedJob.completionReasoning.skipped) {
    deploymentJobSummary.completionReasoning = completedJob.completionReasoning;
  }

  // Sync AI summary from job to fault (customer-facing + technical report)
  // First check the in-memory job object (may have summary from pre-save)
  let customerSummary = completedJob.customerSummary;
  let technicalReport = completedJob.aiSummary?.technicalReport;

  // If summary is missing, try fetching the latest job document from Firestore
  // (summary may have been saved before transition but not in the passed object due to timing)
  if (!customerSummary && !technicalReport && completedJob.id) {
    try {
      const jobSnap = await getDoc(doc(db, 'jobs', completedJob.id));
      if (jobSnap.exists()) {
        const freshJob = jobSnap.data();
        customerSummary = freshJob.customerSummary;
        technicalReport = freshJob.aiSummary?.technicalReport;
      }
    } catch {
      // Non-critical — continue without summary
    }
  }

  // If still no summary and we have field log entries, generate one now
  if (!customerSummary && fieldLogEntries.length > 0) {
    try {
      const { generateJobSummary } = await import('./ai-summary.service');
      // Re-fetch the latest job data for the most complete context
      let jobForSummary = completedJob;
      if (completedJob.id) {
        try {
          const latestSnap = await getDoc(doc(db, 'jobs', completedJob.id));
          if (latestSnap.exists()) {
            jobForSummary = { id: completedJob.id, ...latestSnap.data() } as Job & { quickRaised?: boolean };
          }
        } catch { /* use in-memory job */ }
      }
      const summary = await generateJobSummary(jobForSummary);
      customerSummary = summary.customerSummary;
      technicalReport = summary.technicalReport;
      console.log(`[FaultSync] Generated AI summary for fault ${faultId} from ${fieldLogEntries.length} field log entries`);
    } catch (err) {
      console.warn(`[FaultSync] AI summary generation failed for fault ${faultId}:`, err);
    }
  }

  if (customerSummary) {
    deploymentJobSummary.customerSummary = customerSummary;
  }
  if (technicalReport) {
    deploymentJobSummary.technicalReport = technicalReport;
  }

  // Sync on-site labour cost + materials used back into fault costs
  const onSiteMinutes = completedJob.actualDuration || 0;

  // Materials the tech actually used on site — previously stored on the
  // deployment summary for reference but never rolled into fault.costs,
  // leaving the fault's billing snapshot blind to installed parts.
  const usedMaterialItems: FaultMaterialItem[] = (completedJob.usedMaterials || [])
    .map((m: Material & { description?: string }) => ({
      description: m.name || m.description || 'Material',
      quantity: m.actualQuantity ?? m.quantity ?? 0,
      unitCost: m.unitCost || 0,
    }))
    .filter((m: FaultMaterialItem) => m.quantity > 0);

  const costUpdate: Record<string, unknown> = {};
  if (onSiteMinutes > 0 || usedMaterialItems.length > 0) {
    const currentCosts: FaultCosts = fault.costs || {
      labourMinutes: 0, labourCost: 0, materials: [], materialsCost: 0, totalCost: 0,
    };
    // Skip materials already recorded on the fault (office may have manually
    // added them) — match on description + quantity to avoid double-counting.
    const newMaterials = usedMaterialItems.filter(
      (m) => !(currentCosts.materials || []).some(
        (existing) => existing.description === m.description && existing.quantity === m.quantity
      )
    );
    costUpdate.costs = recalculateCosts({
      ...currentCosts,
      labourMinutes: currentCosts.labourMinutes + onSiteMinutes,
      materials: [...(currentCosts.materials || []), ...newMaterials],
    });
  }

  // Add history entry
  const completionHistoryEntry: InvestigationHistoryEntry = {
    id: crypto.randomUUID(),
    timestamp: now,
    stage: '04-Review',
    author: techName,
    action: isReturnVisit ? 'Deployment — Return Visit Needed' : 'Deployment Completed',
    details: isReturnVisit
      ? `Tech completed on-site work (${onSiteMinutes}min) but flagged return visit: ${completedJob.returnVisitReason || 'No reason specified'}`
      : `Tech completed on-site work. Duration: ${onSiteMinutes}min. ${completedJob.completionNote || ''}`.trim(),
  };

  // Check if the completed job was quick-raised from an already-completed fault.
  // If so, sync data but don't reopen or change the fault's stage/archive status.
  const isQuickRaisedJob = completedJob.quickRaised === true;
  const faultAlreadyCompleted = fault.deploymentJobCompleted === true;

  if (isQuickRaisedJob && faultAlreadyCompleted) {
    // Append completion data without reopening the fault.
    // arrayUnion is safe for concurrent appends — prevents the office's
    // in-flight call notes / client updates / stage advances from being
    // silently overwritten by this completion sync. (FIX_PLAN B1)
    await updateDoc(faultRef, {
      [`quickRaisedCompletions.${completedJob.id}`]: deploymentJobSummary,
      investigationHistory: arrayUnion(completionHistoryEntry),
      ...costUpdate,
      updatedAt: now,
    });
    console.log(`[FaultSync] Synced quick-raised job completion to fault ${faultId} (fault already completed — not reopening)`);
  } else {
    // Standard flow: advance fault to Review and un-archive so it appears in
    // the active pipeline. Office can review billing/deals/close-out. This is
    // the ONE automatic stage move in the pipeline — the office never clicks
    // "the tech finished" — everything else is manual.
    //
    // Wrapped in a transaction with a stage-order check: if an office user
    // has already moved the fault to Review (or parked it) while this
    // completion sync is running, don't regress the stage. Still write the
    // completion summary + history entry.
    // (FIX_PLAN B5; arrayUnion for investigationHistory per B1.)
    await runTransaction(db, async (tx) => {
      const freshSnap = await tx.get(faultRef);
      if (!freshSnap.exists()) return;
      const fresh = freshSnap.data() as Fault;

      const completionData: Record<string, unknown> = {
        deploymentJobCompleted: true,
        deploymentJobCompletedAt: now,
        deploymentJobCompletedBy: techId,
        deploymentJobSummary,
        investigationHistory: arrayUnion(completionHistoryEntry),
        ...costUpdate,
        updatedAt: now,
      };

      // Only advance stage if the fault hasn't already reached Review
      const currentIdx = FAULT_STAGE_ORDER.indexOf(migrateFaultStage(fresh.stage));
      const reviewIdx = FAULT_STAGE_ORDER.indexOf('04-Review');
      if (currentIdx < reviewIdx) {
        completionData.stage = '04-Review' as FaultStage;
        completionData.archived = false;
      }

      tx.update(faultRef, completionData);
    });
    console.log(`[FaultSync] Synced job completion to fault ${faultId} → Review (${isReturnVisit ? 'return visit needed' : 'completed'})`);
  }
}

/**
 * Advance fault to a specific stage (supports skipping levels)
 */
export async function advanceFaultToStage(
  faultId: string,
  newStage: FaultStage,
  advancedBy: string,
  notes?: string
): Promise<void> {
  const now = new Date().toISOString();
  const faultRef = doc(db, 'faults', faultId);
  const faultSnap = await getDoc(faultRef);

  if (!faultSnap.exists()) {
    throw new Error('Fault not found');
  }

  const fault = faultSnap.data() as Fault;

  // Direction-aware history wording. Faults move freely back and forth
  // (and in/out of the Awaiting-Customer parking lane), so a flat "Advanced
  // to X" reads wrong for corrections and parks. Pick the verb from the move.
  const PARKING_LANE: FaultStage = '07-Awaiting-Customer';
  const fromStage = migrateFaultStage(fault.stage);

  // Same-stage no-op — a double-clicked stage button (or a stale UI) must not
  // write a "Advanced to X" entry for a move that didn't happen.
  if (fromStage === newStage) return;
  const fromLabel = FAULT_STAGE_LABELS[fromStage] ?? fault.stage;
  const toLabel = FAULT_STAGE_LABELS[newStage] ?? newStage;
  const fromIdx = FAULT_STAGE_ORDER.indexOf(fromStage);
  const toIdx = FAULT_STAGE_ORDER.indexOf(newStage);

  let action: string;
  if (newStage === PARKING_LANE) {
    action = 'Parked — awaiting customer';
  } else if (fromStage === PARKING_LANE) {
    action = `Resumed to ${toLabel}`;
  } else if (toIdx < fromIdx) {
    action = `Moved back to ${toLabel}`;
  } else {
    action = `Advanced to ${toLabel}`;
  }

  const newHistoryEntry: InvestigationHistoryEntry = {
    id: crypto.randomUUID(),
    timestamp: now,
    stage: newStage,
    author: advancedBy,
    action,
    details: notes || `Stage changed from ${fromLabel} to ${toLabel}`,
  };

  // Check SLA response on first action
  const slaResponseUpdate: Record<string, unknown> = {};
  if (fault.slaResponseMet === undefined && fault.slaResponseDeadline) {
    const met = new Date(now).getTime() <= new Date(fault.slaResponseDeadline).getTime();
    slaResponseUpdate.slaResponseMet = met;
    if (!met) slaResponseUpdate.slaBreached = true;
  }

  await updateDoc(faultRef, {
    stage: newStage,
    // arrayUnion — concurrent appends (job-completion sync, consult
    // responses) must not be clobbered by a read-modify-write spread.
    investigationHistory: arrayUnion(newHistoryEntry),
    ...slaResponseUpdate,
    updatedAt: now,
  });

  // Customer 360 audit log — fire-and-forget. Day 5.
  if (fault.customerId) {
    logCustomerActivity({
      customerId: fault.customerId,
      actorId: advancedBy,
      actorName: resolveActorName(advancedBy),
      entryType: 'stage_change',
      summary: `Fault "${fault.symptom?.slice(0, 60) || 'Unknown'}" → ${toLabel}`,
      sourceEntity: 'fault',
      sourceId: faultId,
      timestamp: now,
    }).catch((err) => console.error('[advanceFaultToStage] customer audit log failed:', err));
  }
}

/**
 * Exit action: Send fault to billing (fee-for-service callout)
 *
 * `detail` carries the itemised capture from BillingCaptureModal — line items,
 * tax mode, and the tax/total the office confirmed. Supplying it REPLACES any
 * previous capture (see buildBillingCaptureUpdate); omitting it entirely is
 * the legacy flag-only path and leaves the billing fields alone.
 *
 * TWIN: sendServiceRequestToBilling in service-request.service.ts. Both go
 * through buildBillingCaptureUpdate — change money behaviour there, not here.
 */
export async function sendFaultToBilling(
  faultId: string,
  sentBy: string,
  notes?: string,
  detail?: BillingCaptureInput
): Promise<void> {
  const now = new Date().toISOString();
  const faultRef = doc(db, 'faults', faultId);
  const faultSnap = await getDoc(faultRef);

  if (!faultSnap.exists()) {
    throw new Error('Fault not found');
  }

  const fault = faultSnap.data() as Fault;

  // Build a human summary for the timeline so anyone reviewing the fault sees
  // what was flagged for billing without opening the billing page.
  const capture = detail ? buildBillingCaptureUpdate(detail) : null;

  const historyEntry: InvestigationHistoryEntry = {
    id: crypto.randomUUID(),
    timestamp: now,
    stage: fault.stage,
    author: sentBy,
    action: 'Sent to Billing',
    details: capture?.summary || notes || 'Fee-for-service callout — sent to billing',
  };

  const update: Record<string, unknown> = {
    sentToBilling: true,
    sentToBillingAt: now,
    sentToBillingBy: sentBy,
    // Land it in the "To Bill" bucket explicitly (the billing page also treats
    // a missing status as to_bill, but being explicit avoids ambiguity).
    billingStatus: fault.billingStatus || 'to_bill',
    // arrayUnion is concurrency-safe (no read-modify-write) AND tolerates a
    // fault doc with no investigationHistory. Daily-audit #1253 C5 + the
    // audit-trail RMW race class (#949 H-1 / #747 H1 / #773 M2/M3).
    investigationHistory: arrayUnion(historyEntry),
    updatedAt: now,
    ...(capture?.fields ?? {}),
  };

  await updateDoc(faultRef, update);
}

/**
 * Exit action: Send fault to deals (requires new solution/replacement)
 */
// NOTE: sendFaultToDeals (flag-only "Sent to Deals" marker) was removed in the
// Jul 2026 redesign. Send to Deal is now a REAL conversion: the fault modal
// opens a seeded DealCreationWizard and linkFaultToDeal() in
// entity-conversion.service.ts stamps sentToDeals + linkedDealId on success —
// which finally activates useDealWorkspace's linked-faults subscription.

/**
 * Save AI-generated follow-up email draft to fault
 */
export async function saveFollowUpEmailDraft(
  faultId: string,
  emailBody: string
): Promise<void> {
  const now = new Date().toISOString();
  const faultRef = doc(db, 'faults', faultId);

  await updateDoc(faultRef, {
    followUpEmailDraft: emailBody,
    updatedAt: now,
  });
}

/**
 * Mark follow-up email as sent
 */
export async function markFollowUpEmailSent(
  faultId: string,
  sentBy: string
): Promise<void> {
  const now = new Date().toISOString();
  const faultRef = doc(db, 'faults', faultId);

  await updateDoc(faultRef, {
    followUpEmailSentAt: now,
    followUpEmailSentBy: sentBy,
    followUpCompleted: true,
    followUpCompletedAt: now,
    updatedAt: now,
  });
}

// ============================================================================
// QUICK RAISE JOB
// ============================================================================

/**
 * Quick-raise a job directly from a fault — regardless of fault stage or
 * archived status.
 *
 * Used for ad-hoc revisits or follow-up callouts without creating a new
 * fault pipeline entry. The job links back to the fault and carries full
 * customer/address/investigation context. Lands as Unscheduled.
 *
 * Does NOT change the fault stage or archive status.
 */
export async function quickRaiseJobFromFault(
  faultId: string,
  userId: string,
  userName: string,
  details: {
    jobType: 'Install' | 'Fault' | 'Pre-Site' | 'Task';
    reason: string;
    estimatedDuration: number;
    preferredTechIds?: string[];
    preferredTechNames?: string[];
    /** Whether the job should go through provisioning first */
    needsProvisioning?: boolean;
  },
): Promise<string> {
  const faultRef = doc(db, 'faults', faultId);
  const faultSnap = await getDoc(faultRef);
  if (!faultSnap.exists()) throw new Error(`Fault ${faultId} not found`);

  const fault = faultSnap.data() as Fault;
  const now = new Date().toISOString();
  const tenantId = fault.tenantId ?? getTenantIdSync();

  // Build briefing history so the tech has full fault context
  const briefingHistory = buildBriefingHistory(fault, faultId);

  const quickRaiseCoords = await resolveFaultJobCoords(fault);

  const newJob: Record<string, unknown> = {
    jobType: 'job',
    client: fault.clientName,
    type: details.jobType,
    status: 'Unscheduled',
    tenantId,

    // Audit timeline — who made the task
    eventLog: [buildJobEvent('created', `${details.jobType} job quick-raised from fault: ${details.reason}`, { actor: userId, actorName: userName })],

    // Location
    loc: addressSuburb({ address: fault.address }), // The town — `address` carries the whole thing
    address: fault.address,
    lat: quickRaiseCoords.lat,
    lng: quickRaiseCoords.lng,
    ...(quickRaiseCoords.formatted
      ? { geocodedAt: new Date().toISOString(), geocodedAddress: quickRaiseCoords.formatted }
      : {}),

    // Scheduling — unscheduled, ops will drag onto calendar
    date: '',
    time: '09:00',
    duration: details.estimatedDuration,

    // Tech preference
    techIds: [],
    techId: '',
    ...(details.preferredTechIds?.length ? {
      schedulingGuidance: {
        preferredTechIds: details.preferredTechIds,
        preferredTechNames: details.preferredTechNames || [],
      },
    } : {}),

    // Services & Notes
    services: fault.serviceType ? [fault.serviceType] : ['Fault Resolution'],
    notes: `QUICK RAISE — ${details.jobType.toUpperCase()}\n${details.reason}\n\nLinked Fault: ${fault.symptom}${fault.clientName ? `\nCustomer: ${fault.clientName}` : ''}`,

    // Customer link
    customerId: fault.customerId || null,

    // Fault Pipeline — bidirectional link
    faultId,
    ...(fault.serviceType ? { faultServiceType: fault.serviceType } : {}),
    ...(fault.servicePlanName ? { faultServicePlanName: fault.servicePlanName } : {}),
    ...(fault.serviceId ? { faultServiceId: fault.serviceId } : {}),

    // Investigation context for the tech
    briefingHistory,

    // Offline copy of the field briefing — see createDeploymentJob.
    ...(fault.techBriefing ? { techBriefing: fault.techBriefing } : {}),

    // Priority from fault severity
    priority: fault.severity === 'critical' ? 'Critical' : fault.severity === 'high' ? 'High' : 'Normal',

    // Metadata
    quickRaised: true,
    quickRaisedReason: details.reason,
    quickRaisedAt: now,
    quickRaisedBy: userId,
    quickRaisedByName: userName,

    // Provisioning path — if user chose provisioning, mark the job accordingly
    ...(details.needsProvisioning ? {
      provisioningStatus: 'pending',
      lastAction: 'sent_to_provisioning',
    } : {}),

    // Defaults
    checklist: [],
    sos: fault.severity === 'critical',
    deleted: false,
    createdAt: now,
  };

  // Auto-attach checklist template (non-blocking)
  const category = fault.serviceType ?? '';
  if (category) {
    try {
      const checkpoints = await getCheckpointsForNewJob(tenantId, category, details.jobType);
      if (checkpoints) newJob.checkpoints = checkpoints;
    } catch {
      // Non-blocking
    }
  }

  // Strip undefined (Firestore rejects them). Deep, not shallow — the nested
  // `briefingHistory.timeline[]` and `metadata` can carry `undefined` from
  // stage-less / action-less investigation-history entries (see PR #1267); a
  // top-level key sweep wouldn't reach them.
  const jobRef = await addDoc(collection(db, 'jobs'), stripUndefinedDeep(newJob));

  // If provisioning path was chosen, create a placeholder provisioning item
  // linked to the fault so it appears on the /provisioning page
  if (details.needsProvisioning) {
    try {
      await addDoc(collection(db, 'provisioning_items'), {
        // tenantId required by Phase 0.5 Session A rules on /provisioning_items.
        // See sibling addDoc above for the same fix + rationale.
        tenantId: getTenantIdSync(),
        dealId: '', // No deal — fault-sourced
        faultId,
        sourceType: 'fault',
        itemName: `Fault Resolution — ${fault.symptom}`,
        quantity: 1,
        status: 'NOT_ORDERED',
        notes: details.reason,
        metadata: {
          jobId: jobRef.id,
          customerName: fault.clientName,
          severity: fault.severity,
          serviceType: fault.serviceType || null,
        },
        createdAt: now,
        updatedAt: now,
      });
    } catch (err) {
      console.error('[PROVISIONING] Failed to create fault provisioning item:', err);
    }
  }

  // Determine provisioning path label for audit trail
  const provisioningPath = details.needsProvisioning ? 'provisioning' : 'scheduling';

  // Log to fault investigation history (non-blocking)
  const historyEntry: InvestigationHistoryEntry = {
    id: crypto.randomUUID(),
    timestamp: now,
    stage: fault.stage || '01-Triage',
    author: userName,
    action: `Quick-raised ${details.jobType} job`,
    details: `${details.reason} [Path: ${provisioningPath === 'provisioning' ? 'Sent to Provisioning' : 'Ready for Scheduling'}]`,
  };

  await updateDoc(faultRef, {
    quickRaisedJobIds: arrayUnion(jobRef.id),
    quickRaisedJobProvisioningPath: provisioningPath,
    investigationHistory: arrayUnion(historyEntry),
    updatedAt: now,
  });

  return jobRef.id;
}

// ============================================================================
// MANUAL COMPLETION
// ============================================================================

/**
 * Manually mark a deployed fault as completed.
 *
 * Used for stuck faults where the deployment job glitched out and
 * syncJobCompletionToFault never fired. Advances the fault to
 * 04-Review with deploymentJobCompleted = true and un-archives it
 * so the office can do final review.
 */
export async function manualCompleteFault(
  faultId: string,
  completedBy: string,
): Promise<void> {
  const now = new Date().toISOString();
  const faultRef = doc(db, 'faults', faultId);
  const faultSnap = await getDoc(faultRef);
  if (!faultSnap.exists()) throw new Error(`Fault ${faultId} not found`);

  const historyEntry: InvestigationHistoryEntry = {
    id: crypto.randomUUID(),
    timestamp: now,
    stage: '04-Review',
    author: completedBy,
    action: 'Manual Completion',
    details: 'Fault manually marked as completed (deployment sync missed)',
  };

  await updateDoc(faultRef, {
    stage: '04-Review' as FaultStage,
    archived: false,
    deploymentJobCompleted: true,
    deploymentJobCompletedAt: now,
    deploymentJobCompletedBy: completedBy,
    investigationHistory: arrayUnion(historyEntry),
    updatedAt: now,
  });
}

// ============================================================================
// Reopen to Field
// ============================================================================

/**
 * Reopen a completed fault and send it back to the field.
 *
 * Used when the original fix didn't hold, a related issue surfaced, or the
 * customer reported the same fault recurring. Rather than raising a brand
 * new fault (loses continuity) or an ad-hoc job (disconnected from the
 * pipeline), this reopens the SAME fault:
 *
 *   1. Snapshots the previous deployment into `reopenHistory[]` so we keep
 *      the full audit trail.
 *   2. Un-archives + moves the stage back to 03-Deploy.
 *   3. Clears `deploymentJobCompleted` so the subsequent deployment flow
 *      takes over cleanly.
 *   4. Appends an investigation history entry so the timeline reflects
 *      the reopen event.
 *
 * Caller is expected to immediately open `FaultDeploymentModal` so the
 * office can schedule the new deployment job. That modal calls
 * `handoffToDeployment()` which will overwrite `deploymentJobId` with the
 * new job and re-archive the fault as part of the standard flow.
 *
 * Requires the fault to have `deploymentJobCompleted === true` — you can't
 * reopen a fault that never went to the field in the first place.
 */
export async function reopenFaultToField(
  faultId: string,
  reason: string,
  reopenedBy: string,
  reopenedByName?: string,
): Promise<void> {
  const trimmedReason = (reason || '').trim();
  if (!trimmedReason) throw new Error('Reason is required to reopen a fault');

  const now = new Date().toISOString();
  const faultRef = doc(db, 'faults', faultId);
  const faultSnap = await getDoc(faultRef);
  if (!faultSnap.exists()) throw new Error(`Fault ${faultId} not found`);

  const fault = faultSnap.data() as Fault;

  if (!fault.deploymentJobCompleted) {
    throw new Error(
      'Fault has no completed deployment — use "Send to field" instead of Reopen',
    );
  }

  // Snapshot the previous deployment before we clear the main fields
  const reopenEntry: FaultReopenEntry = {
    reopenedAt: now,
    reopenedBy,
    reason: trimmedReason,
    ...(reopenedByName ? { reopenedByName } : {}),
    ...(fault.deploymentJobId ? { previousDeploymentJobId: fault.deploymentJobId } : {}),
    ...(fault.deploymentJobCompletedAt
      ? { previousDeploymentCompletedAt: fault.deploymentJobCompletedAt }
      : {}),
    ...(fault.deploymentJobSummary
      ? { previousDeploymentSummary: fault.deploymentJobSummary }
      : {}),
  };

  const historyEntry: InvestigationHistoryEntry = {
    id: crypto.randomUUID(),
    timestamp: now,
    stage: '03-Deploy',
    author: reopenedByName || reopenedBy,
    action: 'Reopened — sent back to field',
    details: trimmedReason,
  };

  await updateDoc(faultRef, {
    stage: '03-Deploy' as FaultStage,
    archived: false,
    deploymentJobCompleted: false,
    // deploymentJobId is intentionally NOT cleared — handoffToDeployment()
    // will overwrite it when the new deployment job is created. Leaving it
    // here avoids a brief "no linked job" state in the UI between reopen
    // and the office filling in the deployment modal.
    reopenHistory: arrayUnion(reopenEntry),
    reopenCount: increment(1),
    investigationHistory: arrayUnion(historyEntry),
    updatedAt: now,
  });
}
